import {
  acquireKioskDeviceLocation,
  cacheDeviceLocation,
  KIOSK_GEO_OPTIONS,
  readCachedDeviceLocation,
} from './deviceLocationCache';
import { captureSalonDevicePosition } from './deviceGeolocation';
import { clearFranchiseFlowAuth, revokeFranchiseFlowServerSession } from './franchiseFlowAuth';

export const FRANCHISE_GEOFENCE_RADIUS_M = 250;
/** How often to re-check device location while the kiosk session is active. */
export const FRANCHISE_GEOFENCE_POLL_MS = 60 * 1000;
/** Consecutive failed/outside checks before forcing logout (reduces GPS flicker). */
export const FRANCHISE_GEOFENCE_STRIKES = 2;

export class FranchiseGeofenceError extends Error {
  constructor(code, message, extras = {}) {
    super(message);
    this.name = 'FranchiseGeofenceError';
    this.code = code;
    this.extras = extras;
  }
}

/** Haversine distance in metres. */
export function distanceMeters(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (Number(d) * Math.PI) / 180;
  const r = 6371000;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(Number(lat2) - Number(lat1));
  const Δλ = toRad(Number(lon2) - Number(lon1));
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  return 2 * r * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function storeCoordsFromSession(session) {
  const storeLat = Number(session?.latitude);
  const storeLng = Number(session?.longitude);
  const hasStore =
    Boolean(session?.has_store_location) ||
    (Number.isFinite(storeLat) && Number.isFinite(storeLng));

  if (!hasStore || !Number.isFinite(storeLat) || !Number.isFinite(storeLng)) return null;

  return {
    storeLat,
    storeLng,
    radius: Number(session.geofence_radius_m) || FRANCHISE_GEOFENCE_RADIUS_M,
    franchise_name: session?.franchise_name || '',
  };
}

export function sessionHasStoreLocation(session) {
  if (!session) return false;
  if (session.has_store_location === true) return true;
  const storeLat = Number(session?.latitude);
  const storeLng = Number(session?.longitude);
  return Number.isFinite(storeLat) && Number.isFinite(storeLng);
}

export function sessionNeedsStoreLocationCapture(session) {
  return Boolean(session?.organization_id) && !sessionHasStoreLocation(session);
}

async function readDevicePositionForGeofence() {
  // 1) Fresh high-accuracy read with Wi‑Fi / low-accuracy fallback
  try {
    const pos = await captureSalonDevicePosition();
    if (Number.isFinite(pos?.latitude) && Number.isFinite(pos?.longitude)) {
      cacheDeviceLocation(pos);
      return pos;
    }
  } catch (_) { /* try cache / kiosk path */ }

  // 2) Prefetch / recent cache (login page warms this)
  const cached = readCachedDeviceLocation();
  if (cached && Number.isFinite(cached.latitude) && Number.isFinite(cached.longitude)) {
    return cached;
  }

  // 3) Last resort: kiosk acquire (may return cache or throw)
  const acquired = await acquireKioskDeviceLocation({
    required: true,
    forceRefresh: true,
    enableHighAccuracy: KIOSK_GEO_OPTIONS.enableHighAccuracy,
    timeout: Math.max(KIOSK_GEO_OPTIONS.timeout, 15000),
    maximumAge: 0,
  });
  if (acquired && Number.isFinite(acquired.latitude) && Number.isFinite(acquired.longitude)) {
    return acquired;
  }
  throw new Error('Could not read device location for store check.');
}

/**
 * Soft geofence check — does not clear the session.
 * GPS read matches legacy nyufaz-analyze-main franchiseGeofence.js.
 */
export async function evaluateFranchiseGeofence(session) {
  // Partner Store toggle: skip GPS gate for Face Identity / Salon / Reels.
  if (session?.geofence_enabled === false) {
    return { ok: true, skipped: true, reason: 'geofence_disabled' };
  }

  const store = storeCoordsFromSession(session);
  if (!store) {
    return {
      ok: false,
      code: 'STORE_LOCATION_REQUIRED',
      message:
        'Store location is not set. Open the Partner dashboard and set Store location first.',
      extras: { franchise_name: session?.franchise_name || '' },
    };
  }

  let device;
  try {
    device = await readDevicePositionForGeofence();
  } catch (err) {
    return {
      ok: false,
      code: 'DEVICE_LOCATION_FAILED',
      message:
        err?.message ||
        'Could not read device location. Allow location access for analyze.nyafaz.com and try again.',
      extras: { franchise_name: store.franchise_name },
    };
  }

  const distance_m = distanceMeters(
    store.storeLat,
    store.storeLng,
    device.latitude,
    device.longitude,
  );
  if (distance_m > store.radius) {
    return {
      ok: false,
      code: 'OUTSIDE_GEOFENCE',
      message: `You are about ${Math.round(distance_m)} m from the registered store. Access is limited to within ${store.radius} m of the outlet.`,
      extras: {
        distance_m: Math.round(distance_m),
        radius_m: store.radius,
        franchise_name: store.franchise_name,
      },
    };
  }

  cacheDeviceLocation({
    latitude: device.latitude,
    longitude: device.longitude,
    accuracy: device.accuracy,
  });

  return {
    ok: true,
    distance_m: Math.round(distance_m),
    radius_m: store.radius,
  };
}

/**
 * After franchise-flow credentials succeed: require store GPS + device within radius.
 * Clears session on failure so the user cannot bypass by navigating away.
 */
export async function enforceFranchiseGeofence(session) {
  const result = await evaluateFranchiseGeofence(session);
  if (!result.ok) {
    if (result.code === 'STORE_LOCATION_REQUIRED') {
      throw new FranchiseGeofenceError(result.code, result.message, result.extras || {});
    }
    await revokeFranchiseFlowServerSession();
    clearFranchiseFlowAuth();
    throw new FranchiseGeofenceError(result.code, result.message, result.extras || {});
  }
  return {
    distance_m: result.distance_m,
    radius_m: result.radius_m,
  };
}

/**
 * Start continuous geofence monitoring for an active kiosk session.
 * Calls onViolation after FRANCHISE_GEOFENCE_STRIKES consecutive failures.
 */
export function startFranchiseGeofenceMonitor(getSession, onViolation, options = {}) {
  const pollMs = Number(options.pollMs) > 0 ? Number(options.pollMs) : FRANCHISE_GEOFENCE_POLL_MS;
  const neededStrikes =
    Number(options.strikes) > 0 ? Number(options.strikes) : FRANCHISE_GEOFENCE_STRIKES;

  let stopped = false;
  let strikes = 0;
  let inFlight = false;
  let timerId = null;

  const fail = (result) => {
    strikes += 1;
    if (strikes < neededStrikes) return;
    onViolation?.({
      code: result.code,
      message: result.message,
      extras: result.extras || {},
    });
  };

  const runCheck = async () => {
    if (stopped || inFlight) return;
    const session = typeof getSession === 'function' ? getSession() : null;
    if (!session?.organization_id) return;
    if (session?.geofence_enabled === false) {
      strikes = 0;
      return;
    }

    inFlight = true;
    try {
      const result = await evaluateFranchiseGeofence(session);
      if (stopped) return;
      if (result.ok) {
        strikes = 0;
        return;
      }
      fail(result);
    } catch (err) {
      if (stopped) return;
      fail({
        code: 'DEVICE_LOCATION_FAILED',
        message: err?.message || 'Location check failed',
        extras: {},
      });
    } finally {
      inFlight = false;
    }
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') void runCheck();
  };

  void runCheck();
  timerId = window.setInterval(() => {
    void runCheck();
  }, pollMs);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);

  return () => {
    stopped = true;
    if (timerId != null) window.clearInterval(timerId);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onVisible);
  };
}
